import { useEffect, useState, type FormEvent } from "react";
import { CircleCheck, Clock3, KeyRound, MapPin, Save, Settings, ShieldCheck, UserRound } from "lucide-react";
import { apiRequest, ApiRequestError } from "../api";
import { useAuth } from "../auth";

export function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setUsername(user.username);
  }, [user?.displayName, user?.username]);

  if (!user) return null;

  const systemAdministrator = user.role === "SYSTEM_ADMINISTRATOR";
  const municipalityAdministrator = user.role === "MUNICIPAL_CITY_ADMINISTRATOR";

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProfileError("");
    setProfileSuccess("");
    setProfileSaving(true);
    try {
      await apiRequest("/account/profile", {
        method: "PUT",
        body: JSON.stringify({ displayName, username }),
      });
      await refreshUser();
      setProfileSuccess("Account information updated successfully.");
    } catch (error) {
      setProfileError(apiErrorMessage(error, "Account information could not be updated."));
    } finally {
      setProfileSaving(false);
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword !== confirmPassword) {
      setPasswordError("The new password and confirmation do not match.");
      return;
    }
    if (newPassword.length < 12) {
      setPasswordError("The new password must contain at least 12 characters.");
      return;
    }

    setPasswordSaving(true);
    try {
      await apiRequest<void>("/account/password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Password updated successfully. Other signed-in browser sessions were signed out.");
    } catch (error) {
      setPasswordError(apiErrorMessage(error, "Password could not be updated."));
    } finally {
      setPasswordSaving(false);
    }
  }

  return <section className="portal-page">
    <header className="page-heading">
      <div>
        <span className="eyebrow">Account and portal preferences</span>
        <h1>Settings</h1>
        <p>Review the secure identity and geographic authority controlling this portal session.</p>
      </div>
      <Settings className="page-heading-icon" />
    </header>

    <div className="settings-grid">
      <section className="form-card">
        <div className="section-heading">
          <h2><UserRound />Signed-in account</h2>
          <p>{municipalityAdministrator ? "Update your own display name and login username. Role, status, and geographic authority remain server-controlled." : "Identity is managed by the central RBIM account service."}</p>
        </div>

        {municipalityAdministrator ? <form onSubmit={saveProfile}>
          <div className="form-grid">
            <label>
              Display name
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                minLength={2}
                maxLength={160}
                autoComplete="name"
                required
              />
            </label>
            <label>
              Username
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                minLength={3}
                maxLength={100}
                pattern="[A-Za-z0-9._-]+"
                title="Use only letters, numbers, periods, underscores, and hyphens."
                autoComplete="username"
                required
              />
            </label>
          </div>
          <dl className="detail-list">
            <div><dt>Role</dt><dd>{formatRole(user.role)}</dd></div>
            <div><dt>Status</dt><dd>{user.status}</dd></div>
          </dl>
          {profileError ? <div className="form-error" role="alert">{profileError}</div> : null}
          {profileSuccess ? <div className="settings-security-note"><CircleCheck /><span>{profileSuccess}</span></div> : null}
          <button className="primary-button" type="submit" disabled={profileSaving || !displayName.trim() || !username.trim()}>
            <Save />{profileSaving ? "Saving..." : "Save Account Changes"}
          </button>
        </form> : <dl className="detail-list">
          <div><dt>Display name</dt><dd>{user.displayName}</dd></div>
          <div><dt>Username</dt><dd>{user.username}</dd></div>
          <div><dt>Role</dt><dd>{formatRole(user.role)}</dd></div>
          <div><dt>Status</dt><dd>{user.status}</dd></div>
        </dl>}
      </section>

      <section className="form-card">
        <div className="section-heading">
          <h2><MapPin />{systemAdministrator ? "Platform location authority" : "Authorized location"}</h2>
          <p>{systemAdministrator ? "This account is anchored to the current deployment for authentication, while System Administrator location management is limited to the approved Municipality/City catalog." : "This value is server-assigned and cannot be changed in the browser."}</p>
        </div>
        <dl className="detail-list">
          <div><dt>{systemAdministrator ? "Deployment anchor" : "Assigned scope"}</dt><dd>{user.locationLabel}</dd></div>
          <div><dt>{systemAdministrator ? "Management coverage" : "Scope type"}</dt><dd>{systemAdministrator ? "APPROVED MUNICIPALITIES / CITIES" : user.scope.type.replaceAll("_", " ")}</dd></div>
        </dl>
      </section>

      {municipalityAdministrator ? <section className="form-card">
        <div className="section-heading">
          <h2><KeyRound />Change password</h2>
          <p>Confirm your current password before setting a new password. Passwords are validated and stored securely by the central RBIM account service.</p>
        </div>
        <form onSubmit={changePassword}>
          <div className="form-grid">
            <label>
              Current password
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <label>
              New password
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                minLength={12}
                maxLength={512}
                autoComplete="new-password"
                required
              />
            </label>
            <label>
              Confirm new password
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={12}
                maxLength={512}
                autoComplete="new-password"
                required
              />
            </label>
          </div>
          {passwordError ? <div className="form-error" role="alert">{passwordError}</div> : null}
          {passwordSuccess ? <div className="settings-security-note"><CircleCheck /><span>{passwordSuccess}</span></div> : null}
          <button className="primary-button" type="submit" disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}>
            <KeyRound />{passwordSaving ? "Updating..." : "Update Password"}
          </button>
        </form>
      </section> : null}

      <section className="form-card">
        <div className="section-heading">
          <h2><ShieldCheck />Session security</h2>
          <p>Authentication, CSRF protection, permission checks, and locality checks remain active.</p>
        </div>
        <div className="settings-security-note"><Clock3 /><span>Inactive portal sessions automatically sign out after 30 minutes.</span></div>
      </section>
    </div>
  </section>;
}

function apiErrorMessage(error: unknown, fallback: string) {
  return error instanceof ApiRequestError ? error.message : fallback;
}

function formatRole(role: string) {
  if (role === "MUNICIPAL_CITY_USER") return "Municipality/City User";
  if (role === "MUNICIPAL_CITY_ADMINISTRATOR") return "Municipality/City Administrator";
  if (role === "BARANGAY_WEB_PORTAL") return "Barangay Web Portal";
  if (role === "BARANGAY_SUPERVISOR") return "Barangay Supervisor";
  if (role === "SYSTEM_ADMINISTRATOR") return "System Administrator";
  if (role === "TECHNICAL_ADMINISTRATOR") return "Technical Administrator";
  return role.replaceAll("_", " ");
}
